import * as Cesium from 'cesium'
import i18n from '../i18n'
import { api } from '../api/client'
import { createBatchedHeightSampler } from './batchedHeightSampler'

/** 지연 로드되는 magoFluid 모듈의 타입 (이 파일의 import 자체는 번들에 모듈을 넣지 않는다) */
export type MagoFluidModule = typeof import('mago-fluid-module')

let modulePromise: Promise<MagoFluidModule> | null = null

/**
 * 모듈과 셰이더(wasm)를 처음 필요할 때 한 번만 내려받아 초기화한다.
 * 로그인·대시보드 첫 로딩에는 영향이 없고, 실패하면 다음에 다시 시도할 수 있다.
 */
export function loadFluidModule(): Promise<MagoFluidModule> {
  if (!modulePromise) {
    modulePromise = (async () => {
      const mod = await import('mago-fluid-module')
      await mod.initMagoShaders() // /mago-shaders/*.wasm
      return mod
    })().catch((e) => {
      modulePromise = null
      throw e
    })
  }
  return modulePromise
}

/**
 * 메쉬 높이 샘플링(sampleHeightMostDetailed) 한 배치를 기다리는 최대 시간.
 * 타일 요청이 끝나지 않으면 이 Promise 가 영영 풀리지 않아 영역 생성 스피너가 계속 도므로, 넘으면 기본 지형 높이로 대체한다.
 */
const HEIGHT_SAMPLE_TIMEOUT_MS = 20_000

export type FluidToastKind = 'success' | 'info' | 'warn' | 'error'

export interface FluidHostContext {
  viewer: Cesium.Viewer
  /** 시뮬레이션 대상 메쉬의 타일셋. 높이는 여기서 샘플링한다. */
  tileset: Cesium.Cesium3DTileset | undefined
  /** 대상 타일셋에서 내용을 불러오지 못한 타일 수. 있으면 DSM 스캔이 끝나지 않을 수 있어 DSM 기본값을 끈다. */
  failedTileCount: number
}

export interface FluidHostHooks {
  toast: (kind: FluidToastKind, message: string) => void
}

/** `.mago` 를 URL 로 불러올 때 허용하는 경로 - 우리 API 의 시뮬레이션 결과 다운로드만 */
export const SIMULATION_DOWNLOAD_PATH = /^\/data\/\d+\/simulations\/\d+\/download$/

export function simulationDownloadPath(meshDataId: number, resultId: number): string {
  return `/data/${meshDataId}/simulations/${resultId}/download`
}

export function currentFluidLanguage(): 'ko' | 'en' {
  return i18n.language?.toLowerCase().startsWith('en') ? 'en' : 'ko'
}

/**
 * 모듈에 호스트 어댑터를 주입한다. 대상 메쉬가 바뀌어도 어댑터는 `getContext()` 로 최신 값을 읽는다.
 *
 * "업로드한 메쉬 기반으로만" 동작하게 하는 부분:
 *  - dsmDefault       : 영역을 만들 때 "DSM 반영"을 기본으로 켠다(타일을 못 불러온 데이터는 제외).
 *                       메쉬 높이가 격자에 들어가는 경로는 이것뿐이다. 모듈이 장면의 타일셋을 GPU 로 스캔해(GpuHeightSampler)
 *                       Cesium 지형 격자 위에 셀마다 max(지형, 스캔값)으로 얹는다. DSM 이 꺼지면 메쉬는 반영되지 않고 공개 지형만 쓴다.
 *  - getTerrainHeight : 모듈의 점 높이 조회(getMagoTerrainHeight)가 Cesium 지형 조회에 실패·시간 초과했을 때만 쓰이는 폴백.
 *                       여기서 메쉬 높이를 구해 주지만 격자 전체에는 영향이 없다(영역 중심 기준 고도, 구조물 위치 등 점 값용).
 *  - http.getBinary   : 우리 API 의 결과 다운로드 경로만 허용
 *  - resolveProjectionDefinition : 외부(epsg.io) 조회 차단
 */
export function configureFluidHost(mod: MagoFluidModule, getContext: () => FluidHostContext, hooks: FluidHostHooks): void {
  mod.setMagoViewer(getContext().viewer)

  const sampleMeshHeight = createBatchedHeightSampler(
    async (points) => {
      const { viewer, tileset } = getContext()
      if (!tileset || tileset.isDestroyed() || viewer.isDestroyed() || !viewer.scene.sampleHeightSupported) {
        return points.map(() => undefined)
      }
      const positions = points.map(({ lon, lat }) => Cesium.Cartographic.fromDegrees(lon, lat))
      const sampled = await viewer.scene.sampleHeightMostDetailed(positions)
      return sampled.map((p) => p?.height)
    },
    (lon, lat) => mod.getMagoTerrainHeight(lon, lat),
    {
      timeoutMs: HEIGHT_SAMPLE_TIMEOUT_MS,
      onBatch: ({ points, ms, outcome }) => {
        const log = outcome === 'ok' ? console.debug : console.warn
        log(`[magoFluid] 메쉬 높이 샘플링 ${outcome}: ${points}점, ${ms}ms${outcome === 'ok' ? '' : ' - 모듈 기본 지형 높이로 대체'}`)
      },
    },
  )

  mod.configureMago({
    getViewer: () => getContext().viewer,
    getTerrainHeight: sampleMeshHeight,
    dsmDefault: () => getContext().failedTileCount === 0,
    language: currentFluidLanguage(),
    features: { recording: true, openProject: true },
    http: {
      getBinary: async (url, init) => {
        if (!SIMULATION_DOWNLOAD_PATH.test(url)) {
          throw new Error('Blocked: only saved simulation results of this service can be loaded.')
        }
        const res = await api.get<ArrayBuffer>(url, { responseType: 'arraybuffer', signal: init?.signal })
        return res.data
      },
    },
    resolveProjectionDefinition: async () => null,
    toast: {
      success: (m) => hooks.toast('success', m),
      info: (m) => hooks.toast('info', m),
      warn: (m) => hooks.toast('warn', m),
      error: (m) => hooks.toast('error', m),
    },
  })
}

export interface SimulationSupport {
  ok: boolean
  /** 지원하지 않는 이유 (번역 키 suffix) */
  reason?: 'desktopOnly' | 'noWebgl' | 'noDrawBuffers'
}

/** 모듈이 요구하는 WebGL1 + WEBGL_draw_buffers(출력 5개)와 데스크톱 화면 여부를 미리 확인한다. */
export function checkSimulationSupport(): SimulationSupport {
  if (typeof window !== 'undefined' && window.matchMedia?.('(max-width: 767px)').matches) {
    return { ok: false, reason: 'desktopOnly' }
  }
  try {
    const canvas = document.createElement('canvas')
    const gl = canvas.getContext('webgl') as WebGLRenderingContext | null
    if (!gl) return { ok: false, reason: 'noWebgl' }
    const ext = gl.getExtension('WEBGL_draw_buffers')
    const buffers = ext ? (gl.getParameter(ext.MAX_DRAW_BUFFERS_WEBGL) as number) : 0
    gl.getExtension('WEBGL_lose_context')?.loseContext()
    return buffers >= 5 ? { ok: true } : { ok: false, reason: 'noDrawBuffers' }
  } catch {
    return { ok: false, reason: 'noWebgl' }
  }
}
